import { type FormEvent, useState } from "react";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { auth } from "../firebase";

type Mode = "signin" | "signup";

/**
 * The way in, for the same accounts the phone app uses.
 *
 * A password account has to prove its address before it can be used — the app enforces the same
 * rule, so an account that skipped it here would simply not work there.
 */
export function AuthPanel({ onBack }: { onBack?: () => void }) {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [verifySent, setVerifySent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    run(async () => {
      if (mode === "signin") {
        const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
        await credential.user.reload();
        if (!credential.user.emailVerified) {
          await auth.signOut();
          throw new Error("Confirm your email address first, then sign in.");
        }
        return;
      }

      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      if (username.trim()) {
        await updateProfile(credential.user, { displayName: username.trim() });
      }
      await sendEmailVerification(credential.user);
      // The account is unusable until the address is confirmed; don't leave a half-signed-in
      // session behind that the app would reject anyway.
      await auth.signOut();
      setVerifySent(true);
    });
  };

  if (verifySent) {
    return (
      <section className="card narrow">
        <h2>Check your email</h2>
        <p className="hint">
          We sent a confirmation link to <strong>{email.trim()}</strong>. Open it, then come back
          and sign in. The app will not let you in until the address is confirmed.
        </p>
        <button
          onClick={() => {
            setVerifySent(false);
            setMode("signin");
          }}
        >
          Back to sign in
        </button>
      </section>
    );
  }

  return (
    <form className="card narrow" onSubmit={submit}>
      <h2>{mode === "signin" ? "Sign in" : "Create an account"}</h2>

      {mode === "signup" && (
        <label>
          Your name
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="How your coach will see you"
            autoComplete="nickname"
          />
        </label>
      )}

      <label>
        Email
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          required
        />
      </label>
      <label>
        Password
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete={mode === "signin" ? "current-password" : "new-password"}
          minLength={8}
          required
        />
      </label>

      {error && <p className="error">{error}</p>}

      <button type="submit" disabled={busy}>
        {busy ? "Working…" : mode === "signin" ? "Sign in" : "Create account"}
      </button>

      <button
        type="button"
        className="ghost"
        disabled={busy}
        onClick={() => run(async () => void (await signInWithPopup(auth, new GoogleAuthProvider())))}
      >
        Continue with Google
      </button>

      <button
        type="button"
        className="ghost"
        onClick={() => {
          setMode(mode === "signin" ? "signup" : "signin");
          setError(null);
        }}
      >
        {mode === "signin" ? "I need an account" : "I already have an account"}
      </button>

      {onBack && (
        <button type="button" className="ghost" onClick={onBack}>
          Back
        </button>
      )}
    </form>
  );
}
